/**
 * FormBuilder Vuex Store Module
 * 
 * Manages all business state for the form builder feature:
 * - Home page: Form list, filters, pagination
 * - Create/Edit: Form elements, mappings, current form data
 * - Shared: Mappable types, element definitions
 * 
 * Architecture:
 * - State: Business data (forms, current form, mappings)
 * - Getters: Computed derived state
 * - Mutations: Synchronous state changes (UPPER_CASE)
 * - Actions: Async operations and complex logic (camelCase)
 * 
 * File Handling:
 * - Image/Logo files are uploaded separately before form save
 * - FileIds are stored in backend, converted to blobs on load
 */

import { prepareFormDataForSave, processFormDataAfterLoad } from '@/utils/formBuilder/fileHandlers';
import moment from 'moment';
import {
  listFormsApi,
  getFormByIdApi,
  createFormApi,
  updateFormApi,
  getMappableFormElementsApi,
  getCategoriesApi,
  archiveFormApi
} from '@/api/formBuilder/formApis';
import {
  handleError,
  handleFormSaveError,
  handleFormLoadError,
  validateFormData
} from '@/utils/formBuilder/errorHandlers';

/**
 * Helper: Calculate time range (start_date, end_date) based on filter settings
 * @param {Object} filters - homePageFilters object
 * @returns {Object} - { start_date, end_date } in ISO 8601 format or undefined
 */
function getTimeRangeFromFilter(filters) {
  const { filterTime, customStartDate, customEndDate } = filters;
  
  if (filterTime === 'all') {
    return { start_date: undefined, end_date: undefined };
  }
  
  if (filterTime === 'custom') {
    return {
      start_date: customStartDate ? moment(customStartDate).utc().toISOString() : undefined,
      end_date: customEndDate ? moment(customEndDate).utc().toISOString() : undefined
    };
  }
  
  // Calculate time range for preset filters
  const now = moment.utc();
  const endDate = now.toISOString();
  let startDate;
  
  switch (filterTime) {
    case 'today':
      // Start of today in local time, converted to UTC
      startDate = moment().startOf('day').utc().toISOString();
      break;
      
    case 'week':
      // 7 days ago
      startDate = now.clone().subtract(7, 'days').toISOString();
      break;
      
    case 'month':
      // 30 days ago
      startDate = now.clone().subtract(30, 'days').toISOString();
      break;

    case 'quarter':
      // 90 days ago
      startDate = now.clone().subtract(90, 'days').toISOString();
      break;
      
    default:
      return { start_date: undefined, end_date: undefined };
  }
  
  return { start_date: startDate, end_date: endDate };
}

export default {
  namespaced: true,
  
  state: {
    // ═══════════════════════════════════════════════════════════
    // HOME PAGE STATE
    // ═══════════════════════════════════════════════════════════
    
    /**
     * List of forms displayed on home page
     * @type {Array<Object>}
     * Structure: [{ id, name, description, category, emoji_tag, status, version, updated_at, created_at }]
     */
    homePageForms: [],
    
    /**
     * Loading state for home page forms list
     * @type {Boolean}
     */
    isLoadingForms: false,
    
    /**
     * Error message for home page operations
     * @type {String|null}
     */
    homePageError: null,
    
    /**
     * Home page filter state
     */
    homePageFilters: {
      searchQuery: '',
      filterType: 'all',      // 'all' | category key from API
      filterTime: 'all',      // 'all' | 'today' | 'week' | 'month' | 'custom'
      customStartDate: null,  // ISO 8601 string for custom range
      customEndDate: null,    // ISO 8601 string for custom range
      status: 'ACTIVE'        // 'ACTIVE' | 'ARCHIVED' | 'DELETED'
    },
    
    /**
     * Home page pagination state
     */
    homePagePagination: {
      currentPage: 1,
      pageSize: 5,
      totalCount: 0
    },
    
    /**
     * Available form categories from backend
     * @type {Array<{key: string, display_name: string}>}
     */
    formCategories: [],
    
    /**
     * Loading state for categories
     * @type {Boolean}
     */
    isLoadingCategories: false,
    
    /**
     * Cache for form details (full definition with fields)
     * Key: form ID, Value: form object with definition.fields
     * Prevents redundant API calls when previewing/editing forms
     * @type {Object<string, Object>}
     */
    formDetailsCache: {},
    
    // ═══════════════════════════════════════════════════════════
    // CREATE/EDIT DOCUMENT STATE
    // ═══════════════════════════════════════════════════════════
    
    /**
     * Current form being created or edited
     * @type {Object|null}
     * Structure: { id, name, description, category, emoji_tag, status, version }
     */
    currentForm: null,
    
    /**
     * Title of the current form being edited
     * @type {String}
     */
    currentFormTitle: 'Untitled Form',
    
    /**
     * Array of form elements (fields) in the current form
     * @type {Array<Object>}
     * Structure: [{ type, label, id, data: {...} }]
     */
    formElements: [],

    /**
     * The editing state of form elements
     * @type {Object}
     * Structure: { [id]: true }
     */
    editingStateOfFormElements: {},
    
    /**
     * Currently selected element ID
     * Used for syncing selection between preview and mapping panel
     * @type {String|null}
     */
    selectedElementId: null,
    
    /**
     * Currently selected mapping item ID
     * @type {String|null}
     */
    selectedMappingItemId: null,
    
    /**
     * Loading state for save/update operations
     * @type {Boolean}
     */
    isSavingForm: false,
    
    /**
     * Error message for create/edit operations
     * @type {String|null}
     */
    formEditorError: null,
    
    // ═══════════════════════════════════════════════════════════
    // SHARED CONFIGURATION STATE
    // ═══════════════════════════════════════════════════════════
    
    /**
     * Available patient variables for mapping (from backend)
     * @type {Array<Object>}
     * Structure: [{ key, display_label, category, write_behavior, allowed_field_types }]
     * 
     * Note: MAPPABLE_TYPES is now derived from this data via getter
     */
    mappableVariables: [],
    
    /**
     * Loading state for mappable variables
     * @type {Boolean}
     */
    isLoadingMappableVariables: false,
  },
  
  // ═══════════════════════════════════════════════════════════
  // GETTERS - Computed derived state
  // ═══════════════════════════════════════════════════════════
  getters: {
    homePageTotalCount: (state) => state.homePagePagination.totalCount,
    /**
     * Get unique element types that can be mapped
     * Derived from mappableVariables.allowed_field_types
     */
    MAPPABLE_TYPES: (state) => {
      if (!state.mappableVariables || state.mappableVariables.length === 0) {
        return [];
      }
      
      const types = new Set();
      state.mappableVariables.forEach(variable => {
        if (variable.allowed_field_types && Array.isArray(variable.allowed_field_types)) {
          variable.allowed_field_types.forEach(type => types.add(type));
        }
      });
      
      return Array.from(types);
    },
    
    /**
     * Get mapping options organized by element type
     * Returns { [elementType]: [variables that support this type] }
     * Example: { 'email': [{ key: 'patient_email', ... }], 'numeric': [...] }
     */
    mappingOptionsByType: (state) => {
      if (!state.mappableVariables || state.mappableVariables.length === 0) {
        return {};
      }
      
      const optionsByType = {};
      
      state.mappableVariables.forEach(variable => {
        if (variable.allowed_field_types && Array.isArray(variable.allowed_field_types)) {
          variable.allowed_field_types.forEach(type => {
            if (!optionsByType[type]) {
              optionsByType[type] = [];
            }
            optionsByType[type].push(variable);
          });
        }
      });
      
      return optionsByType;
    },
    
    /**
     * Get mapping options for a specific element type
     * Filters out mappings already used by other elements
     * 
     * @param {String} type - Element type (e.g., 'email', 'short-response')
     * @param {String} currentElementId - ID of current element (to allow keeping its own mapping)
     * @returns {Array} - Array of available variables for this type
     */
    getMappingOptionsForType: (state, getters) => (type, currentElementId = null) => {
      const allOptions = getters.mappingOptionsByType[type] || [];
      
      // Get all mappings currently in use by OTHER elements
      const usedMappings = new Set();
      state.formElements.forEach(element => {
        // Skip the current element (allow it to keep its own mapping)
        if (element.id === currentElementId) {
          return;
        }
        // Add this element's mapping to the used set
        if (element.data?.mapping) {
          usedMappings.add(element.data.mapping);
        }
      });
      
      // Filter out options that are already mapped by other elements
      return allOptions.filter(option => !usedMappings.has(option.key));
    },
    
    /**
     * Get elements that can be mapped (for mapping panel)
     */
    mappableElements: (state, getters) => {
      return state.formElements.filter(el => getters.MAPPABLE_TYPES.includes(el.type));
    },
    
    /**
     * Check if a type is mappable
     */
    isMappableType: (state, getters) => (type) => {
      return getters.MAPPABLE_TYPES.includes(type);
    },
    
    /**
     * Get element by ID
     */
    getElementById: (state) => (id) => {
      return state.formElements.find(el => el.id === id);
    },

    /**
     * Check element's editing status by ID
     */
    isElementEditing: (state) => (id) => {
      // console.log('isElementEditing', state.editingStateOfFormElements, id);
      return state.editingStateOfFormElements[id] === true;
    },
  },
  
  // ═══════════════════════════════════════════════════════════
  // MUTATIONS - Synchronous state changes (UPPER_CASE naming)
  // ═══════════════════════════════════════════════════════════
  mutations: {
    // ─────────────────────────────────────────────────────────
    // Home Page Mutations
    // ─────────────────────────────────────────────────────────
    
    SET_HOME_PAGE_FORMS(state, forms) {
      state.homePageForms = forms;
    },
    
    SET_IS_LOADING_FORMS(state, isLoading) {
      state.isLoadingForms = isLoading;
    },
    
    SET_HOME_PAGE_ERROR(state, error) {
      state.homePageError = error;
    },
    
    SET_SEARCH_QUERY(state, query) {
      state.homePageFilters.searchQuery = query;
    },
    
    SET_FILTER_TYPE(state, type) {
      state.homePageFilters.filterType = type;
    },
    
    SET_FILTER_TIME(state, time) {
      state.homePageFilters.filterTime = time;
    },
    
    SET_CUSTOM_DATE_RANGE(state, { startDate, endDate }) {
      state.homePageFilters.customStartDate = startDate;
      state.homePageFilters.customEndDate = endDate;
    },
    
    SET_FORM_CATEGORIES(state, categories) {
      state.formCategories = categories;
    },
    
    SET_IS_LOADING_CATEGORIES(state, isLoading) {
      state.isLoadingCategories = isLoading;
    },
    
    SET_CACHED_FORM_DETAILS(state, { formId, formDetails }) {
      state.formDetailsCache = {
        ...state.formDetailsCache,
        [formId]: formDetails
      };
    },
    
    CLEAR_FORM_DETAILS_CACHE(state) {
      state.formDetailsCache = {};
    },
    
    REMOVE_CACHED_FORM_DETAILS(state, formId) {
      const cache = { ...state.formDetailsCache };
      delete cache[formId];
      state.formDetailsCache = cache;
    },
    
    SET_FILTER_STATUS(state, status) {
      state.homePageFilters.status = status;
    },
    
    SET_CURRENT_PAGE(state, page) {
      state.homePagePagination.currentPage = page;
    },
    
    SET_PAGE_SIZE(state, size) {
      state.homePagePagination.pageSize = size;
    },
    
    SET_TOTAL_COUNT(state, count) {
      state.homePagePagination.totalCount = count;
    },
    
    RESET_HOME_PAGE_FILTERS(state) {
      state.homePageFilters = {
        searchQuery: '',
        filterType: 'all',
        status: 'ACTIVE'
      };
      state.homePagePagination.currentPage = 1;
    },
    
    // ─────────────────────────────────────────────────────────
    // Create/Edit Document Mutations
    // ─────────────────────────────────────────────────────────
    
    SET_CURRENT_FORM(state, form) {
      state.currentForm = form;
    },
    
    SET_CURRENT_FORM_TITLE(state, title) {
      state.currentFormTitle = title;
    },
    
    SET_FORM_ELEMENTS(state, elements) {
      state.formElements = elements;
    },
    
    ADD_FORM_ELEMENT(state, { element, index }) {
      state.formElements.splice(index, 0, element);
    },
    
    REMOVE_FORM_ELEMENT(state, index) {
      state.formElements.splice(index, 1);
    },
    
    REMOVE_FORM_ELEMENT_BY_ID(state, id) {
      const index = state.formElements.findIndex(el => el.id === id);
      if (index !== -1) {
        state.formElements.splice(index, 1);
      }
    },
    
    UPDATE_FORM_ELEMENT(state, { index, element }) {
      state.formElements.splice(index, 1, element);
    },
    
    UPDATE_ELEMENT_DATA(state, { index, data }) {
      if (index >= 0 && index < state.formElements.length) {
        state.formElements[index].data = { 
          ...state.formElements[index].data, 
          ...data 
        };
      }
    },
    
    UPDATE_ELEMENT_MAPPING(state, { id, mapping }) {
      const element = state.formElements.find(el => el.id === id);
      if (element && element.data) {
        if (mapping && mapping.trim() !== '') {
          // Set mapping if value provided
          element.data.mapping = mapping;
        } else {
          // Delete mapping field if empty
          delete element.data.mapping;
        }
      }
    },
    
    UPDATE_ELEMENT_EDITING_STATE(state, { id, isEditing }) {
      state.editingStateOfFormElements[id] = isEditing;
    },

    CLEAR_ELEMENT_EDITING_STATE(state) {
      state.editingStateOfFormElements = {};
    },

    REMOVE_ELEMENT_EDITING_STATE(state, id) {
      delete state.editingStateOfFormElements[id];
    },
    
    REORDER_FORM_ELEMENTS(state, { fromIndex, toIndex }) {
      const [moved] = state.formElements.splice(fromIndex, 1);
      state.formElements.splice(toIndex, 0, moved);
    },
    
    SET_SELECTED_ELEMENT_ID(state, id) {
      state.selectedElementId = id;
    },
    
    SET_SELECTED_MAPPING_ITEM_ID(state, itemId) {
      state.selectedMappingItemId = itemId;
    },
    
    SET_IS_SAVING_FORM(state, isSaving) {
      state.isSavingForm = isSaving;
    },
    
    SET_FORM_EDITOR_ERROR(state, error) {
      state.formEditorError = error;
    },
    
    RESET_FORM_EDITOR(state) {
      state.currentForm = null;
      state.currentFormTitle = 'Untitled Form';
      state.formElements = [];
      state.selectedElementId = null;
      state.selectedMappingItemId = null;
      state.formEditorError = null;
    },
    
    // ─────────────────────────────────────────────────────────
    // Shared Configuration Mutations
    // ─────────────────────────────────────────────────────────
    
    SET_MAPPABLE_VARIABLES(state, variables) {
      state.mappableVariables = variables;
    },
    
    SET_IS_LOADING_MAPPABLE_VARIABLES(state, isLoading) {
      state.isLoadingMappableVariables = isLoading;
    },
  },
  
  // ═══════════════════════════════════════════════════════════
  // ACTIONS - Async operations and complex logic (camelCase naming)
  // ═══════════════════════════════════════════════════════════
  actions: {
    /**
     * Initialize home page - load forms list
     * Fetches forms from backend with filters and pagination
     */
    async initHomePage({ commit, state }) {
      commit('SET_IS_LOADING_FORMS', true);
      commit('SET_HOME_PAGE_ERROR', null);
      
      try {
        // Calculate time range based on filter
        const { start_date, end_date } = getTimeRangeFromFilter(state.homePageFilters);
        
        const response = await listFormsApi({
          status: state.homePageFilters.status,
          category: state.homePageFilters.filterType !== 'all' ? state.homePageFilters.filterType : undefined,
          search: state.homePageFilters.searchQuery || undefined,
          start_date,
          end_date,
          limit: state.homePagePagination.pageSize,
          offset: (state.homePagePagination.currentPage - 1) * state.homePagePagination.pageSize
        });
        
        // Note: List endpoint doesn't include full definition
        // File conversion happens when individual form is loaded via getFormByIdApi
        commit('SET_HOME_PAGE_FORMS', response.items);
        commit('SET_TOTAL_COUNT', response.total);
      } catch (error) {
        const parsedError = handleError(error, 'loading forms');
        commit('SET_HOME_PAGE_ERROR', parsedError.message);
      } finally {
        commit('SET_IS_LOADING_FORMS', false);
      }
    },
    
    /**
     * Update search query and reset to first page
     */
    updateSearchQuery({ commit }, query) {
      commit('SET_SEARCH_QUERY', query);
      commit('SET_CURRENT_PAGE', 1);
    },
    
    /**
     * Update filter type and reset to first page
     */
    updateFilterType({ commit }, type) {
      commit('SET_FILTER_TYPE', type);
      commit('SET_CURRENT_PAGE', 1);
    },
    
    /**
     * Update time filter and reset to first page
     */
    updateFilterTime({ commit }, time) {
      commit('SET_FILTER_TIME', time);
      commit('SET_CURRENT_PAGE', 1);
    },
    
    /**
     * Update custom date range and reset to first page
     */
    updateCustomDateRange({ commit }, { startDate, endDate }) {
      commit('SET_CUSTOM_DATE_RANGE', { startDate, endDate });
      commit('SET_FILTER_TIME', 'custom');
      commit('SET_CURRENT_PAGE', 1);
    },
    
    /**
     * Load form categories from backend
     * Used to populate category filter dropdown
     */
    async loadCategories({ commit }) {
      commit('SET_IS_LOADING_CATEGORIES', true);
      
      try {
        const categories = await getCategoriesApi();
        commit('SET_FORM_CATEGORIES', categories);
      } catch (error) {
        handleError(error, 'loading categories', { silent: false });
        
        // Fallback to empty array - component can show "No categories" message
        commit('SET_FORM_CATEGORIES', []);
      } finally {
        commit('SET_IS_LOADING_CATEGORIES', false);
      }
    },
    
    /**
     * Load form details (with definition.fields) for preview or edit
     * Uses cache-first strategy to avoid redundant API calls
     * 
     * @param {String} formId - ID of the form to load
     * @param {Boolean} forceRefresh - Skip cache and force API call
     * @returns {Promise<Object>} - Form details with definition.fields
     */
    async loadFormDetails({ commit, state }, { formId, forceRefresh = false }) {
      // Check cache first (unless force refresh)
      if (!forceRefresh && state.formDetailsCache[formId]) {
        console.log('[FormBuilder Store] Using cached form details for:', formId);
        // Use structuredClone to preserve File/Blob objects
        return structuredClone(state.formDetailsCache[formId]);
      }
      
      try {
        console.log('[FormBuilder Store] Fetching form details from API:', formId);
        
        // 1. Load form from backend (contains fileIds in definition.fields)
        const formFromBackend = await getFormByIdApi(formId);
        
        // 2. Convert fileIds to blob URLs for display
        // This downloads image/logo files and creates blob URLs
        const processedForm = await processFormDataAfterLoad(formFromBackend);
        
        // 3. Cache the processed form details
        commit('SET_CACHED_FORM_DETAILS', { 
          formId, 
          formDetails: processedForm 
        });
        
        console.log('[FormBuilder Store] Form details loaded and cached:', formId);
        // Use structuredClone to preserve File/Blob objects
        return structuredClone(processedForm);
      } catch (error) {
        const parsedError = handleFormLoadError(error, formId);
        throw parsedError;
      }
    },
    
    /**
     * Change page
     */
    changePage({ commit }, page) {
      commit('SET_CURRENT_PAGE', page);
    },
    
    /**
     * Change page size and reset to first page
     */
    changePageSize({ commit }, size) {
      commit('SET_PAGE_SIZE', size);
      commit('SET_CURRENT_PAGE', 1);
    },
    
    /**
     * Load mappable variables from backend
     * Fetches available EHR variables that can be mapped to form fields
     */
    async loadMappableVariables({ commit }) {
      commit('SET_IS_LOADING_MAPPABLE_VARIABLES', true);
      
      try {
        const variables = await getMappableFormElementsApi();
        commit('SET_MAPPABLE_VARIABLES', variables);
      } catch (error) {
        handleError(error, 'loading mappable variables', { silent: false });
        
        // Fallback to mock data if API fails (for development/testing)
        console.warn('[FormBuilder Store] Using fallback mock data for mappable variables');
        const mockVariables = [
          {
            key: 'patient_first_name',
            display_label: 'Patient First Name',
            category: 'Demographics',
            write_behavior: 'if_empty',
            allowed_field_types: ['short-response', 'long-response']
          },
          {
            key: 'patient_last_name',
            display_label: 'Patient Last Name',
            category: 'Demographics',
            write_behavior: 'if_empty',
            allowed_field_types: ['short-response', 'long-response']
          },
          {
            key: 'patient_age',
            display_label: 'Patient Age',
            category: 'Demographics',
            write_behavior: 'if_empty',
            allowed_field_types: ['numeric', 'short-response']
          },
          {
            key: 'patient_dob',
            display_label: 'Patient Date of Birth',
            category: 'Demographics',
            write_behavior: 'if_empty',
            allowed_field_types: ['date', 'datetime', 'time']
          },
          {
            key: 'patient_email',
            display_label: 'Patient Email',
            category: 'Contact Information',
            write_behavior: 'if_empty',
            allowed_field_types: ['email']
          },
          {
            key: 'patient_phone',
            display_label: 'Patient Phone Number',
            category: 'Contact Information',
            write_behavior: 'if_empty',
            allowed_field_types: ['phone']
          },
          {
            key: 'patient_gender',
            display_label: 'Patient Gender',
            category: 'Demographics',
            write_behavior: 'if_empty',
            allowed_field_types: ['single-selection', 'dropdown']
          },
          {
            key: 'appointment_notes',
            display_label: 'Appointment Notes',
            category: 'Appointment',
            write_behavior: 'always',
            allowed_field_types: ['long-response', 'description']
          },
          {
            key: 'clinic_name',
            display_label: 'Clinic Name',
            category: 'Clinic Information',
            write_behavior: 'always',
            allowed_field_types: ['clinic-branding', 'section-header']
          },
          {
            key: 'patient_consent',
            display_label: 'Patient Consent',
            category: 'Legal',
            write_behavior: 'if_empty',
            allowed_field_types: ['checkbox', 'yes-no']
          },
          {
            key: 'help_instructions',
            display_label: 'Help Instructions',
            category: 'Instructions',
            write_behavior: 'always',
            allowed_field_types: ['note-help', 'description']
          },
          {
            key: 'patient_photo',
            display_label: 'Patient Photo',
            category: 'Demographics',
            write_behavior: 'if_empty',
            allowed_field_types: ['image']
          }
        ];
        commit('SET_MAPPABLE_VARIABLES', mockVariables);
      } finally {
        commit('SET_IS_LOADING_MAPPABLE_VARIABLES', false);
      }
    },
    
    /**
     * Initialize form editor (create new or load existing)
     * Loads form and converts fileIds to blob URLs for editing
     * Uses loadFormDetails for cache-first loading
     */
    async initFormEditor({ commit, dispatch }, formId = null) {
      commit('RESET_FORM_EDITOR');
      
      if (formId) {
        commit('SET_IS_SAVING_FORM', true); // Use as loading indicator
        
        try {
          // Use loadFormDetails which handles caching
          const processedForm = await dispatch('loadFormDetails', { formId });
          
          // Update store with processed form data
          commit('SET_CURRENT_FORM', processedForm);
          commit('SET_CURRENT_FORM_TITLE', processedForm.name);
          commit('SET_FORM_ELEMENTS', processedForm.definition?.fields || []);
          
          console.log('[FormBuilder Store] Form editor initialized:', formId);
        } catch (error) {
          commit('SET_FORM_EDITOR_ERROR', error.message);
          throw error; // Re-throw to allow UI to handle
        } finally {
          commit('SET_IS_SAVING_FORM', false);
        }
      }
    },
    
    /**
     * Add element to form at specific index
     */
    addElement({ commit }, { element, index }) {
      commit('ADD_FORM_ELEMENT', { element, index });
    },
    
    /**
     * Remove element by index
     */
    removeElement({ commit, state }, index) {
      const removedElement = state.formElements[index];
      commit('REMOVE_FORM_ELEMENT', index);
      commit('REMOVE_ELEMENT_EDITING_STATE', removedElement.id);
      
      // Clear selection if removed element was selected
      if (removedElement && removedElement.id === state.selectedElementId) {
        commit('SET_SELECTED_ELEMENT_ID', null);
        commit('SET_SELECTED_MAPPING_ITEM_ID', null);
      }
    },
    
    /**
     * Remove element by ID
     */
    removeElementById({ commit, state }, id) {
      commit('REMOVE_FORM_ELEMENT_BY_ID', id);
      commit('REMOVE_ELEMENT_EDITING_STATE', id);
      
      // Clear selection if removed element was selected
      if (id === state.selectedElementId) {
        commit('SET_SELECTED_ELEMENT_ID', null);
        commit('SET_SELECTED_MAPPING_ITEM_ID', null);
      }
    },
    
    /**
     * Update element data
     */
    updateElementData({ commit }, { index, data }) {
      commit('UPDATE_ELEMENT_DATA', { index, data });
    },
    
    /**
     * Update element mapping
     */
    updateElementMapping({ commit }, { id, mapping }) {
      commit('UPDATE_ELEMENT_MAPPING', { id, mapping });
    },

    /**
     * Update element editing state
     */
    updateElementEditingState({ commit }, { id, isEditing }) {
      commit('UPDATE_ELEMENT_EDITING_STATE', { id, isEditing });
    },
    
    /**
     * Reorder elements
     */
    reorderElements({ commit }, { fromIndex, toIndex }) {
      commit('REORDER_FORM_ELEMENTS', { fromIndex, toIndex });
    },
    
    /**
     * Select element (sync between preview and mapping)
     */
    selectElement({ commit }, id) {
      commit('SET_SELECTED_ELEMENT_ID', id);
      commit('SET_SELECTED_MAPPING_ITEM_ID', id);
    },
    
    /**
     * Deselect element
     */
    deselectElement({ commit }) {
      commit('SET_SELECTED_ELEMENT_ID', null);
      commit('SET_SELECTED_MAPPING_ITEM_ID', null);
    },
    
    /**
     * Save form (create or update)
     * Uploads image/logo files first, then saves form with fileIds
     */
    async saveForm({ commit, state }) {
      commit('SET_IS_SAVING_FORM', true);
      commit('SET_FORM_EDITOR_ERROR', null);
      
      try {
        // 1. Prepare initial form data
        const formData = {
          name: state.currentFormTitle,
          description: state.currentForm?.description,
          category: state.currentForm?.category,
          emoji_tag: state.currentForm?.emoji_tag,
          definition: {
            fields: state.formElements
          }
        };        
        // 1.5. Validate form data before proceeding
        validateFormData(formData);
        
        // 2. Upload files and replace blob URLs with fileIds
        // This extracts imageFile/logoFile (File objects), uploads them,
        // and replaces with imageFileId/logoFileId
        const preparedFormData = await prepareFormDataForSave(formData);
        console.log('preparedFormData', preparedFormData);
        
        // 3. Save form to backend with fileIds
        const isUpdate = !!state.currentForm?.id;
        let savedForm;
        
        if (isUpdate) {
          // Update existing form
          console.log('[FormBuilder Store] Updating form:', state.currentForm.id);
          savedForm = await updateFormApi(state.currentForm.id, preparedFormData);
        } else {
          // Create new form
          console.log('[FormBuilder Store] Creating new form');
          savedForm = await createFormApi(preparedFormData);
        }

        //4. Update store with saved form
        commit('SET_CURRENT_FORM', savedForm);
        commit('SET_CURRENT_FORM_TITLE', savedForm.name);
        
        // 5. Update form details cache with the saved form
        // Process the saved form to convert fileIds to blobs for display
        const processedSavedForm = await processFormDataAfterLoad(savedForm);
        commit('SET_CACHED_FORM_DETAILS', { 
          formId: savedForm.id, 
          formDetails: processedSavedForm 
        });
        
        console.log('[FormBuilder Store] Form saved successfully and cache updated:', savedForm?.id);
        return { success: true, form: savedForm };
      } catch (error) {
        const parsedError = handleFormSaveError(error, !!state.currentForm?.id);
        commit('SET_FORM_EDITOR_ERROR', parsedError.message);
        return { success: false, error: parsedError };
      } finally {
        commit('SET_IS_SAVING_FORM', false);
      }
    },
    
    /**
     * Archive a form (ACTIVE → ARCHIVED)
     * Removes form from active list after successful archive
     * 
     * @param {String} formId - ID of the form to archive
     * @returns {Promise<Object>} - { success: boolean, form?: Object, error?: Object }
     */
    async archiveForm({ commit, dispatch }, formId) {
      try {
        console.log('[FormBuilder Store] Archiving form:', formId);
        
        // Call archive API
        const archivedForm = await archiveFormApi(formId);
        
        console.log('[FormBuilder Store] Form archived successfully:', formId);
        
        // Refresh the forms list to remove the archived form
        await dispatch('initHomePage');
        
        return { success: true, form: archivedForm };
      } catch (error) {
        const parsedError = handleError(error);
        console.error('[FormBuilder Store] Failed to archive form:', parsedError);
        return { success: false, error: parsedError };
      }
    },
  }
};
